import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CHART_RENDERING_AVAILABLE } from '../../addons/chartRendering';
import { useAuth } from '../../hooks/useAuth';
import { canEdit as canEditWithRoles, canUseTransformations } from '../../utils/capabilities';

import {
  dataObjectApi,
  DataObjectDto,
  DataObjectInsightsDto,
  DataObjectVersionDto,
} from '../../utils/dataObjectApi';
import {
  workspaceApi,
  WorkspaceFolder,
  WorkspacePermission,
  WorkspaceResourceType,
} from '../../utils/workspaceApi';
import { addonApi, gisArtifactApi } from '../../utils/addonApi';
import { AddonExtension } from '../../types/addons';
import { getCompiledHostSlotExtensions } from '../../addons/compiledAddonRegistry';
import DatasetProvenancePanel from '../DatasetProvenancePanel';
import AccessManagementSection from './AccessManagementSection';
import TagEditor from './TagEditor';
import { ExplorerItemKind, KindAvatar, kindLabel } from './itemKinds';

const FULL_DATA_PAGE_SIZE = 100;

export type DrawerResource = {
  kind: ExplorerItemKind;
  /** Folder id or item id depending on kind. */
  id: string;
  name: string;
  subtext?: string;
  folder?: WorkspaceFolder;
  dataObject?: DataObjectDto | null;
  raw?: any;
};

type PreviewResponse = { rows: Record<string, unknown>[] };

type QueryResult = {
  columns: { name: string; type: string }[];
  rows: Record<string, unknown>[];
  totalRows: number;
};

const formatDateTime = (value?: string | null): string => {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatBytes = (value?: number | null): string => {
  if (value == null || value < 0) {
    return '—';
  }
  if (value < 1024) {
    return `${value} B`;
  }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = value;
  let idx = -1;
  do {
    size /= 1024;
    idx += 1;
  } while (size >= 1024 && idx < units.length - 1);
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[idx]}`;
};

const formatCount = (value?: number | null): string =>
  value == null || Number.isNaN(value) ? '—' : new Intl.NumberFormat().format(value);

type InsightsPhase = 'loading' | 'ready' | 'unavailable';

/**
 * A dataset metric from the insights read. "Not recorded" is reserved for a successful answer that
 * carries no value: the platform neither stored one nor could compute it for this request.
 */
const insightMetric = (phase: InsightsPhase, value: number | null | undefined, format: (v: number) => string) => {
  if (phase === 'loading') return 'Loading…';
  if (phase === 'unavailable') return 'Unavailable';
  return value == null ? 'Not recorded' : format(value);
};

const MetaField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Grid>
);

type ItemDetailsDrawerProps = {
  open: boolean;
  resource: DrawerResource | null;
  permission: WorkspacePermission | null;
  tags: string[];
  onTagsSaved: (resourceType: WorkspaceResourceType, resourceKey: string, tags: string[]) => void;
  callerUsername?: string | null;
  onAccessSaved?: () => void | Promise<void>;
  onClose: () => void;
  /** Increment to force the full-data dialog open (deep links). */
  openFullDataSignal?: number;
};

/**
 * Slide-out inspector for the selected explorer row: metadata, tags, access management,
 * and (for datasets) preview, version history, provenance and actions.
 */
const ItemDetailsDrawer: React.FC<ItemDetailsDrawerProps> = ({
  open,
  resource,
  permission,
  tags,
  onTagsSaved,
  callerUsername = null,
  onAccessSaved,
  onClose,
  openFullDataSignal = 0,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const dataObject = resource?.dataObject || null;
  const isFolder =
    resource?.kind === 'folder' ||
    resource?.kind === 'personal-root' ||
    resource?.kind === 'shared-root';
  const resourceType: WorkspaceResourceType = isFolder ? 'FOLDER' : 'ITEM';
  const resourceKey = resource
    ? isFolder
      ? resource.id
      : `${resource.kind}:${resource.id}`
    : '';
  const canEdit = permission === 'EDIT' || permission === 'MANAGE';

  const [insights, setInsights] = useState<DataObjectInsightsDto | null>(null);
  const [insightsPhase, setInsightsPhase] = useState<InsightsPhase>('loading');
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [versions, setVersions] = useState<DataObjectVersionDto[]>([]);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [dataObjectActions, setDataObjectActions] = useState<AddonExtension[]>([]);
  const [gisActionLoading, setGisActionLoading] = useState(false);
  const [gisActionMessage, setGisActionMessage] = useState<string | null>(null);
  const [gisActionError, setGisActionError] = useState<string | null>(null);

  const [versionPreview, setVersionPreview] = useState<{
    open: boolean;
    version: number | null;
    rows: Record<string, unknown>[];
    loading: boolean;
    error: string | null;
  }>({ open: false, version: null, rows: [], loading: false, error: null });

  const [fullDataOpen, setFullDataOpen] = useState(false);
  const [fullDataRows, setFullDataRows] = useState<Record<string, unknown>[]>([]);
  const [fullDataColumns, setFullDataColumns] = useState<string[]>([]);
  const [fullDataPage, setFullDataPage] = useState(0);
  const [fullDataLoading, setFullDataLoading] = useState(false);
  const [fullDataError, setFullDataError] = useState<string | null>(null);

  useEffect(() => {
    setInsights(null);
    setInsightsPhase('loading');
    setPreviewRows([]);
    setPreviewError(null);
    setVersions([]);
    setVersionsError(null);
    setDataObjectActions([]);
    setGisActionMessage(null);
    setGisActionError(null);
    setFullDataOpen(false);
    setVersionPreview({ open: false, version: null, rows: [], loading: false, error: null });

    if (!dataObject) {
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);

    void dataObjectApi
      .get<DataObjectInsightsDto>(`/api/v1/data-objects/${dataObject.id}/insights`)
      .then((value) => {
        if (cancelled) return;
        setInsights(value);
        setInsightsPhase('ready');
      })
      .catch(() => {
        if (!cancelled) setInsightsPhase('unavailable');
      });

    void dataObjectApi
      .get<PreviewResponse>(`/api/v1/data-objects/${dataObject.id}/preview?limit=25`)
      .then((value) => {
        if (!cancelled) {
          setPreviewRows(Array.isArray(value?.rows) ? value.rows.slice(0, 25) : []);
        }
      })
      .catch((reason: any) => {
        if (!cancelled) {
          setPreviewError(
            reason?.response?.data?.message || reason?.message || 'Failed to load dataset preview.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });

    void dataObjectApi
      .get<DataObjectVersionDto[]>(`/api/v1/data-objects/${dataObject.id}/versions`)
      .then((value) => {
        if (!cancelled) setVersions(Array.isArray(value) ? value : []);
      })
      .catch(() => {
        if (!cancelled) setVersionsError('Failed to load version history.');
      });

    void addonApi
      .listExtensions('dataObject.readAction', dataObject.id)
      .then((value) => {
        if (!cancelled && Array.isArray(value)) {
          setDataObjectActions(getCompiledHostSlotExtensions(value, 'dataObject.readAction'));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [dataObject]);

  const loadFullDataPage = useCallback(
    async (page: number) => {
      if (!dataObject) {
        return;
      }
      setFullDataLoading(true);
      setFullDataError(null);
      try {
        const offset = page * FULL_DATA_PAGE_SIZE;
        const sql = `SELECT * FROM data LIMIT ${FULL_DATA_PAGE_SIZE} OFFSET ${offset}`;
        const response = await dataObjectApi.post<QueryResult>(
          `/api/v1/data-objects/${dataObject.id}/query`,
          { sql, limit: FULL_DATA_PAGE_SIZE }
        );
        const rows = Array.isArray(response?.rows) ? response.rows : [];
        const mappedColumns = Array.isArray(response?.columns)
          ? response.columns.map((column) => column.name)
          : [];
        setFullDataRows(rows);
        setFullDataColumns(
          mappedColumns.length > 0 ? mappedColumns : rows.length > 0 ? Object.keys(rows[0]) : []
        );
        setFullDataPage(page);
      } catch (e: any) {
        setFullDataError(
          e?.response?.data?.message || e?.message || 'Failed to load full dataset page.'
        );
      } finally {
        setFullDataLoading(false);
      }
    },
    [dataObject]
  );

  useEffect(() => {
    if (!fullDataOpen || !dataObject) {
      return;
    }
    void loadFullDataPage(fullDataPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullDataOpen, dataObject, fullDataPage]);

  useEffect(() => {
    if (openFullDataSignal > 0 && dataObject) {
      setFullDataRows([]);
      setFullDataColumns([]);
      setFullDataError(null);
      setFullDataPage(0);
      setFullDataOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFullDataSignal]);

  const openVersionPreview = useCallback(
    async (version: number) => {
      if (!dataObject) {
        return;
      }
      setVersionPreview({ open: true, version, rows: [], loading: true, error: null });
      try {
        const preview = await dataObjectApi.get<PreviewResponse>(
          `/api/v1/data-objects/${dataObject.id}/versions/${version}/preview?limit=25`
        );
        const rows = Array.isArray(preview?.rows) ? preview.rows.slice(0, 25) : [];
        setVersionPreview((prev) =>
          prev.open && prev.version === version ? { ...prev, rows, loading: false } : prev
        );
      } catch (e: any) {
        const message =
          e?.response?.data?.message || e?.message || 'Failed to load version preview.';
        setVersionPreview((prev) =>
          prev.open && prev.version === version
            ? { ...prev, loading: false, error: message }
            : prev
        );
      }
    },
    [dataObject]
  );

  const previewColumns = useMemo(
    () => (previewRows.length === 0 ? [] : Object.keys(previewRows[0])),
    [previewRows]
  );

  // The Studio action opens its chart builder for this dataset, which is hidden while charts
  // cannot be drawn; the Studio stays reachable from navigation for existing charts.
  const visualisationAction = CHART_RENDERING_AVAILABLE
    ? dataObjectActions.find((extension) => extension.addonId === 'stratforth.visualisation')
    : undefined;
  const gisAction = dataObjectActions.find(
    (extension) => extension.addonId === 'stratforth.gis'
  );

  const totalRows = insights?.rowCount ?? null;
  const hasNextPage =
    totalRows != null
      ? (fullDataPage + 1) * FULL_DATA_PAGE_SIZE < totalRows
      : fullDataRows.length === FULL_DATA_PAGE_SIZE;
  const startRow = fullDataPage * FULL_DATA_PAGE_SIZE + 1;
  const endRow = fullDataPage * FULL_DATA_PAGE_SIZE + fullDataRows.length;

  if (!resource) {
    return null;
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480, md: 560 }, p: 0 } }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <KindAvatar kind={resource.kind} size={40} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap title={resource.name}>
              {resource.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {kindLabel(resource.kind)}
              {resource.subtext ? ` • ${resource.subtext}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close details">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, overflowY: 'auto' }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Details
              </Typography>
              <Grid container spacing={1.5}>
                {dataObject ? (
                  <>
                    <MetaField label="Type" value={dataObject.fileType || '—'} />
                    <MetaField
                      label="Version"
                      value={`v${insights?.version ?? dataObject.version}`}
                    />
                    <MetaField
                      label="Uploaded"
                      value={formatDateTime(insights?.uploadedAt || dataObject.createdAt)}
                    />
                    <MetaField
                      label="Last edited"
                      value={formatDateTime(insights?.updatedAt || dataObject.updatedAt)}
                    />
                    <MetaField label="Last used" value={formatDateTime(insights?.lastUsedAt)} />
                    <MetaField
                      label="File size"
                      value={insightMetric(insightsPhase, insights?.fileSizeBytes, formatBytes)}
                    />
                    <MetaField
                      label="Rows"
                      value={insightMetric(insightsPhase, insights?.rowCount, formatCount)}
                    />
                    <MetaField
                      label="Description"
                      value={dataObject.description || '—'}
                    />
                  </>
                ) : isFolder ? (
                  <>
                    <MetaField
                      label="Created"
                      value={formatDateTime(resource.folder?.createdAt)}
                    />
                    <MetaField
                      label="Last edited"
                      value={formatDateTime(resource.folder?.updatedAt)}
                    />
                    <MetaField
                      label="Created by"
                      value={resource.folder?.createdBy || '—'}
                    />
                    <MetaField
                      label="Owner"
                      value={
                        resource.folder?.ownerUsername
                          ? `${resource.folder.ownerUsername} (personal)`
                          : 'Organisation'
                      }
                    />
                  </>
                ) : (
                  <>
                    <MetaField label="Type" value={resource.subtext || kindLabel(resource.kind)} />
                    <MetaField
                      label="Created"
                      value={formatDateTime(resource.raw?.createdAt)}
                    />
                    <MetaField
                      label="Last edited"
                      value={formatDateTime(resource.raw?.updatedAt)}
                    />
                    <MetaField label="Status" value={resource.raw?.status || '—'} />
                  </>
                )}
              </Grid>
              {permission && (
                <Chip
                  size="small"
                  sx={{ mt: 1.5 }}
                  color={permission === 'MANAGE' ? 'success' : 'default'}
                  label={
                    permission === 'MANAGE'
                      ? 'You have full access'
                      : permission === 'EDIT'
                        ? 'You can edit'
                        : 'You can view'
                  }
                />
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <TagEditor
                tags={tags}
                canEdit={canEdit}
                onSave={async (nextTags) => {
                  const result = await workspaceApi.setTags(resourceType, resourceKey, nextTags);
                  onTagsSaved(resourceType, resourceKey, result.tags);
                }}
              />
              {!canEdit && tags.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No tags.
                </Typography>
              )}
            </Box>

            <Divider />

            {(isFolder || dataObject) && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Who has access
                </Typography>
                <AccessManagementSection
                  resourceType={resourceType}
                  resourceKey={resourceKey}
                  callerUsername={callerUsername}
                  onSaved={onAccessSaved}
                />
              </Box>
            )}

            {dataObject && (
              <>
                <Divider />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canEditWithRoles(user?.roles) && canUseTransformations(user?.roles) && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/transformations?inputDataObjectId=${encodeURIComponent(dataObject.id)}`
                        )
                      }
                    >
                      Transform Dataset
                    </Button>
                  )}
                  {visualisationAction && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate(
                          `/visualisations?dataObjectId=${encodeURIComponent(dataObject.id)}`,
                          {
                            state: {
                              dataObjectId: dataObject.id,
                              dataObjectName: dataObject.name,
                            },
                          }
                        )
                      }
                    >
                      {typeof visualisationAction.manifest.label === 'string'
                        ? visualisationAction.manifest.label
                        : 'Inspect in Visualisation Studio'}
                    </Button>
                  )}
                  {gisAction && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={gisActionLoading}
                      onClick={async () => {
                        setGisActionLoading(true);
                        setGisActionMessage(null);
                        setGisActionError(null);
                        try {
                          const response = await gisArtifactApi.readDataObjectMapAction(
                            dataObject.id
                          );
                          setGisActionMessage(
                            `Map preview ready for ${response.previewRows.length} rows.`
                          );
                        } catch (e: any) {
                          setGisActionError(
                            e?.response?.data?.message ||
                              e?.message ||
                              'Failed to load GIS map preview.'
                          );
                        } finally {
                          setGisActionLoading(false);
                        }
                      }}
                    >
                      {typeof gisAction.manifest.label === 'string'
                        ? gisAction.manifest.label
                        : 'View on map'}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => {
                      setFullDataRows([]);
                      setFullDataColumns([]);
                      setFullDataError(null);
                      setFullDataPage(0);
                      setFullDataOpen(true);
                    }}
                  >
                    Open Full Data
                  </Button>
                </Stack>

                {gisActionMessage && <Alert severity="info">{gisActionMessage}</Alert>}
                {gisActionError && <Alert severity="error">{gisActionError}</Alert>}

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview (max 25 rows)
                  </Typography>
                  {previewError && <Alert severity="error">{previewError}</Alert>}
                  {previewLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : previewRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No preview rows available.
                    </Typography>
                  ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {previewColumns.map((column) => (
                              <TableCell key={column}>{column}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewRows.map((row, index) => (
                            <TableRow key={`preview-${index}`}>
                              {previewColumns.map((column) => (
                                <TableCell key={`${index}-${column}`}>
                                  {row[column] == null ? '' : String(row[column])}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Version history
                  </Typography>
                  {versionsError && <Alert severity="error">{versionsError}</Alert>}
                  {versions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No version history available.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Version</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>By</TableCell>
                          <TableCell align="right">Preview</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {versions.map((version) => {
                          const currentVersion = insights?.version ?? dataObject.version;
                          const isCurrent = version.version === currentVersion;
                          return (
                            <TableRow key={`version-${version.version}`}>
                              <TableCell>
                                v{version.version}
                                {isCurrent ? ' (current)' : ''}
                              </TableCell>
                              <TableCell>{version.fileType}</TableCell>
                              <TableCell>{formatDateTime(version.createdAt)}</TableCell>
                              <TableCell>{version.createdBy || '—'}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  aria-label={`Preview version ${version.version}`}
                                  onClick={() => void openVersionPreview(version.version)}
                                >
                                  Preview
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Box>

                <DatasetProvenancePanel
                  dataObjectId={dataObject.id}
                  // Only trust insights for the CURRENTLY selected object: on an A->B switch
                  // insights still holds A's data until B's resolve, and the panel must never
                  // query B with A's version.
                  version={
                    insights && insights.id === dataObject.id
                      ? insights.version
                      : dataObject.version
                  }
                  datasetName={dataObject.name}
                />
              </>
            )}
          </Stack>
        </Box>
      </Drawer>

      <Dialog
        open={fullDataOpen}
        onClose={() => setFullDataOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{dataObject?.name || 'Dataset'} - Full Data</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Showing rows {fullDataRows.length > 0 ? startRow : 0} - {endRow} of{' '}
                {totalRows != null ? formatCount(totalRows) : 'unknown'}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={fullDataLoading || fullDataPage === 0}
                  onClick={() => setFullDataPage((page) => Math.max(0, page - 1))}
                >
                  Previous 100
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={fullDataLoading || !hasNextPage}
                  onClick={() => setFullDataPage((page) => page + 1)}
                >
                  Next 100
                </Button>
              </Stack>
            </Stack>

            {fullDataError && <Alert severity="error">{fullDataError}</Alert>}

            {fullDataLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : fullDataRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No rows returned for this page.
              </Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {fullDataColumns.map((column) => (
                        <TableCell key={column}>{column}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fullDataRows.map((row, rowIndex) => (
                      <TableRow key={`full-${rowIndex}`}>
                        {fullDataColumns.map((column) => (
                          <TableCell key={`${rowIndex}-${column}`}>
                            {row[column] == null ? '' : String(row[column])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullDataOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={versionPreview.open}
        onClose={() =>
          setVersionPreview({ open: false, version: null, rows: [], loading: false, error: null })
        }
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {versionPreview.version != null
            ? `Version v${versionPreview.version} preview (max 25 rows)`
            : 'Version preview'}
        </DialogTitle>
        <DialogContent dividers>
          {versionPreview.error && <Alert severity="error">{versionPreview.error}</Alert>}
          {versionPreview.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : versionPreview.rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No preview rows available for this version.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {Object.keys(versionPreview.rows[0]).map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {versionPreview.rows.map((row, index) => (
                    <TableRow key={`version-preview-${index}`}>
                      {Object.keys(versionPreview.rows[0]).map((column) => (
                        <TableCell key={`${index}-${column}`}>
                          {row[column] == null ? '' : String(row[column])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setVersionPreview({
                open: false,
                version: null,
                rows: [],
                loading: false,
                error: null,
              })
            }
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemDetailsDrawer;
